import { Clear } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateProductsMutation,
  useDeleteProductsMutation,
  useUpdateProductsMutation,
} from "src/_api/productApi";
import { runJoiValidate } from "src/_utils";
import validationSchemas from "./validationSchemas";

export default function ProductDialog({ open, onClose, product }) {
  const { t } = useTranslation();
  const [productValue, setProductValue] = useState({
    name: "",
    price: 0,
    sku: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [
    createProduct,
    {
      isLoading: isCreateProductLoading,
      isSuccess: isCreateProductSuccess,
      error: isCreateProductError,
      reset: CreateProductReset,
    },
  ] = useCreateProductsMutation();
  const handleCreateProduct = async () => {
    try {
      const validationErrors = runJoiValidate(
        validationSchemas(),
        productValue,
      );

      setValidationErrors(validationErrors);
      console.log(validationErrors);

      if (validationErrors) return;
      await createProduct(productValue).unwrap();

      setTimeout(() => {
        setProductValue({ name: "", price: 0, sku: "" });
        setValidationErrors(null);
        onClose();
      }, 700);
    } catch (error) {
      console.log(error);
    }
  };
  const [
    updateProduct,
    {
      isLoading: isUpdateProductLoading,
      isSuccess: isUpdateProductSuccess,
      error: isUpdateProductError,
      reset: UpdateProductReset,
    },
  ] = useUpdateProductsMutation();

  const handleUpdateProduct = async () => {
    try {
      const validationErrors = runJoiValidate(
        validationSchemas(),
        productValue,
      );

      setValidationErrors(validationErrors);

      if (validationErrors) return;

      await updateProduct({
        id: product?.id,
        payload: { ...productValue },
      }).unwrap();

      setTimeout(() => {
        onClose();
        setValidationErrors(null);
      }, 700);
    } catch (Error) {
      console.log(Error);
    }
  };

  const [
    deleteProduct,
    {
      isLoading: isDeleteProductLoading,
      isSuccess: isDeleteProductSuccess,
      error: isDeleteProductError,
      reset: DeleteProductReset,
    },
  ] = useDeleteProductsMutation();

  const loading =
    isCreateProductLoading || isUpdateProductLoading || isDeleteProductLoading;
  const error =
    isCreateProductError || isUpdateProductError || isDeleteProductError;
  const isSuccess =
    isCreateProductSuccess || isUpdateProductSuccess || isDeleteProductSuccess;
  const handleDeleteProduct = async () => {
    try {
      await deleteProduct(product?.id).unwrap();

      setTimeout(() => {
        onClose();
      }, 700);
    } catch (Error) {
      console.log(Error);
    }
  };

  useEffect(() => {
    if (product) {
      setProductValue({
        name: product.name || "",
        sku: product.sku || "",
        price: product.price || 0,
      });
    } else {
      setProductValue({
        name: "",
        sku: "",
        price: 0,
      });
    }
  }, [product]);

  useEffect(() => {
    if (!open) {
      CreateProductReset();
      UpdateProductReset();
      DeleteProductReset();
    }
  }, [open]);
  const isEdit = !!product;
  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      onClose={(e) => e.preventDefault()}
      product={product}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          py: 2,
          px: 3,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {isEdit ? t("Update Product") : t("Add Product")}
        </Typography>

        <IconButton
          sx={{
            ml: "auto",
          }}
          onClick={onClose}
        >
          <Clear />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              label={t("Product name")}
              fullWidth
              size="small"
              margin="dense"
              variant="outlined"
              value={productValue.name}
              onChange={(e) =>
                setProductValue({
                  ...productValue,
                  name: e.target.value,
                })
              }
              name="name"
              error={!!validationErrors?.name}
              helperText={validationErrors?.name}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("SKU")}
              value={productValue.sku || ""}
              margin="dense"
              onChange={(e) =>
                setProductValue({ ...productValue, sku: e.target.value })
              }
              name="sku"
              size="small"
              error={!!validationErrors?.sku}
              helperText={validationErrors?.sku}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("Price")}
              margin="dense"
              value={productValue.price || 0}
              onChange={(e) =>
                setProductValue({
                  ...productValue,
                  price: Number(e.target.value),
                })
              }
              name="price"
              size="small"
              error={!!validationErrors?.price}
              helperText={validationErrors?.price}
              fullWidth
              required
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Box flex={1} textAlign="center">
          {!!error && <Typography color="error">{error}</Typography>}
          {isSuccess && (
            <Typography color="success.main">
              {t("Done successfully")}
            </Typography>
          )}
        </Box>{" "}
        <Box mr="auto" />
        <LoadingButton onClick={onClose} variant="contained">
          {t("Cancel")}
        </LoadingButton>
        {isEdit ? (
          <>
            <LoadingButton
              color="error"
              variant="contained"
              onClick={handleDeleteProduct}
            >
              {t("Delete")}
            </LoadingButton>

            <LoadingButton
              loading={loading}
              variant="contained"
              onClick={handleUpdateProduct}
            >
              {t("Update")}
            </LoadingButton>
          </>
        ) : (
          <LoadingButton
            loading={loading}
            variant="contained"
            onClick={handleCreateProduct}
          >
            {t("Add")}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
